import type { components } from "@colossus/api-client";
import {
  DEFAULT_CATALOG,
  FREE_SKU,
  LEVELING_ADVANCED_GRANT,
  LIMIT_IMAGES,
  TIER_PRO_SKU,
  TITAN_BANDS,
  TITAN_S_SKU,
} from "@colossus/contracts";
import { count } from "./format.ts";
import type { Locale, Message } from "./locale.ts";
import { m } from "./paraglide/messages.js";
import { PLAN_SECTIONS } from "./plan-sections.ts";
import type { PlanRow } from "./plan-sections.ts";

type Schemas = components["schemas"];

export type Offer = Schemas["BillingOfferView"];

export type ProductPrice = NonNullable<Schemas["ProductPriceView"]>;

export const priceAmount = (locale: Locale, price: ProductPrice): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: price.currency,
    trailingZeroDisplay: "stripIfInteger",
  }).format(price.amountCents / 100);

export const priceLabel = (locale: Locale, price: ProductPrice): string => {
  const amount = priceAmount(locale, price);
  if (price.interval === "month") return m.price_per_month({ amount }, { locale });
  if (price.interval === "year") return m.price_per_year({ amount }, { locale });
  return amount;
};

export const monthlyShare = (price: ProductPrice): ProductPrice =>
  price.interval === "year"
    ? { ...price, amountCents: Math.round(price.amountCents / 12), interval: "month" }
    : price;

export const SEED_OFFERS: readonly Offer[] = DEFAULT_CATALOG.map((seed) => ({
  sku: seed.sku,
  kind: seed.kind,
  rank: seed.rank,
  grants: seed.grants,
  price: null,
}));

export const offerOf = (offers: readonly Offer[] | null, sku: string): Offer | null => {
  if (offers === null) return null;
  const found = offers.find((entry) => entry.sku === sku);
  return found === undefined ? null : found;
};

const grantValue = (offer: Offer, key: string): boolean | number | undefined =>
  offer.grants.find((grant) => grant.key === key)?.value;

export type CompareCell =
  | { readonly kind: "flag"; readonly held: boolean }
  | { readonly kind: "days"; readonly days: number }
  | { readonly kind: "count"; readonly count: number }
  | { readonly kind: "unbounded" }
  | { readonly kind: "request" }
  | { readonly kind: "text"; readonly label: Message };

export interface CompareRow {
  readonly label: Message;
  readonly cells: readonly CompareCell[];
}

const limitRow = (
  columns: readonly Offer[],
  label: Message,
  key: string,
  cell: (value: number) => CompareCell,
  last: CompareCell | null,
): readonly CompareRow[] => {
  const values = columns.map((offer) => grantValue(offer, key));
  if (!values.every((value) => typeof value === "number")) return [];
  const cells = values.map(cell);
  if (last !== null) cells[cells.length - 1] = last;
  return [{ label, cells }];
};

const countRow = (
  columns: readonly Offer[],
  label: Message,
  key: string,
  unboundedBy: string | undefined,
): readonly CompareRow[] => {
  const cells = columns.map((offer): CompareCell | null => {
    if (unboundedBy !== undefined && grantValue(offer, unboundedBy) === true) {
      return { kind: "unbounded" };
    }
    const held = grantValue(offer, key);
    return typeof held === "number" ? { kind: "count", count: held } : null;
  });
  return cells.every((cell) => cell !== null) ? [{ label, cells }] : [];
};

const imagesRow = (columns: readonly Offer[], label: Message): CompareRow => ({
  label,
  cells: columns.map((offer): CompareCell => {
    const held = grantValue(offer, LIMIT_IMAGES);
    return grantValue(offer, LEVELING_ADVANCED_GRANT) === true && typeof held === "number"
      ? { kind: "count", count: held }
      : { kind: "flag", held: grantValue(offer, LEVELING_ADVANCED_GRANT) === true };
  }),
});

export const differs = (row: CompareRow): boolean =>
  row.cells.some((cell) => JSON.stringify(cell) !== JSON.stringify(row.cells[0]));

export const compared = (row: CompareRow): boolean =>
  differs(row) || row.cells.some((cell) => cell.kind === "request");

interface CompareGroup {
  readonly label: Message | null;
  readonly rows: readonly CompareRow[];
}

export interface CompareSection {
  readonly title: Message;
  readonly groups: readonly CompareGroup[];
}

const flagRow = (
  columns: readonly Offer[],
  label: Message,
  keys: readonly string[],
): CompareRow => ({
  label,
  cells: columns.map((offer) => ({
    kind: "flag",
    held: keys.every((key) => grantValue(offer, key) === true),
  })),
});

const heldRow = (columns: readonly Offer[], label: Message): CompareRow => ({
  label,
  cells: columns.map((): CompareCell => ({ kind: "flag", held: true })),
});

const rowsOf = (columns: readonly Offer[], spec: PlanRow): readonly CompareRow[] => {
  switch (spec.kind) {
    case "flag":
      return [flagRow(columns, spec.label, spec.keys)];
    case "held":
      return [heldRow(columns, spec.label)];
    case "request":
      return [{ label: spec.label, cells: columns.map((): CompareCell => ({ kind: "request" })) }];
    case "days": {
      const last = columns.at(-1);
      const unbounded =
        columns.length === 3 && last !== undefined && grantValue(last, spec.unboundedBy) === true;
      return limitRow(
        columns,
        spec.label,
        spec.key,
        (days) => ({ kind: "days", days }),
        unbounded ? { kind: "unbounded" } : null,
      );
    }
    case "count":
      return countRow(columns, spec.label, spec.key, spec.unboundedBy);
    case "images":
      return [imagesRow(columns, spec.label)];
    case "tiered":
      return [
        {
          label: spec.label,
          cells: columns.map((offer): CompareCell => {
            const reached = spec.levels.findLast(
              (level) => level.key === null || grantValue(offer, level.key) === true,
            );
            return reached === undefined
              ? { kind: "flag", held: false }
              : { kind: "text", label: reached.label };
          }),
        },
      ];
  }
};

export const compareSections = (
  offers: readonly Offer[] | null,
  band: string = TITAN_S_SKU,
): readonly CompareSection[] => {
  const free = offerOf(offers, FREE_SKU);
  const pro = offerOf(offers, TIER_PRO_SKU);
  const titan = offerOf(offers, band);
  if (free === null || pro === null) return [];
  const columns = titan === null ? [free, pro] : [free, pro, titan];
  const rows = (specs: readonly PlanRow[]): readonly CompareRow[] =>
    specs.flatMap((spec) => rowsOf(columns, spec));
  return PLAN_SECTIONS.flatMap((section) => {
    const groups = section.groups.flatMap((group): readonly CompareGroup[] => {
      const kept = [...rows(group.core), ...rows(group.rows), ...rows(group.held)];
      return kept.length === 0 ? [] : [{ label: group.label, rows: kept }];
    });
    return groups.length === 0 ? [] : [{ title: section.title, groups }];
  });
};

export interface TitanSize {
  readonly sku: string;
  readonly price: ProductPrice;
  readonly label: string;
}

export const titanSizes = (
  locale: Locale,
  priceOf: (sku: string) => ProductPrice | null,
): readonly TitanSize[] =>
  TITAN_BANDS.flatMap((band, at) => {
    const price = priceOf(band.sku);
    if (price === null) return [];
    const amount = priceLabel(locale, price);
    const below = TITAN_BANDS[at - 1];
    const label =
      band.maxMembers !== null
        ? m.pricing_titan_size_upto({ price: amount, members: count(locale, band.maxMembers) })
        : below !== undefined && below.maxMembers !== null
          ? m.pricing_titan_size_over({ price: amount, members: count(locale, below.maxMembers) })
          : amount;
    return [{ sku: band.sku, price, label }];
  });
